import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useSearch } from "@/api/notify";
import type { SearchKind } from "@/api/types/SearchKind";
import { Icon, type IconName } from "./Icon";
import { cx, Spinner } from "./ui";

const OPEN_EVENT = "mixla:open-search";
const KINDS: SearchKind[] = ["project", "task", "case", "bug"];
const KIND_ICON: Record<SearchKind, IconName> = { project: "folder", task: "test", case: "list", bug: "bug" };

/** Opens the palette from anywhere (e.g. a search button). */
export function openSearch() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** Search button for the shell, showing the ⌘K shortcut. */
export function SearchButton({ tone = "nav" }: { tone?: "nav" | "surface" }) {
  const { t } = useTranslation();
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <button
      type="button"
      onClick={openSearch}
      aria-label={t("search.label")}
      aria-keyshortcuts={mac ? "Meta+K" : "Control+K"}
      className={cx(
        "flex items-center gap-2 rounded-[var(--radius-ctl)] border px-2.5 py-1.5 text-sm",
        tone === "nav"
          ? "w-full border-nav-border text-nav-muted hover:bg-nav-active hover:text-nav-fg"
          : "border-border text-muted hover:bg-surface-2",
      )}
    >
      <Icon name="search" size={14} />
      <span className="flex-1 text-left">{t("search.open")}</span>
      <kbd className="rounded border border-current px-1 font-mono text-[12px]">{mac ? "⌘K" : "Ctrl K"}</kbd>
    </button>
  );
}

type Item = { id: string; icon: IconName; label: ReactNode; sub?: string; path: string };

/** ⌘K / Ctrl+K global search. Mounted once per signed-in shell. */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);
  return open ? <Palette onClose={() => setOpen(false)} /> : null;
}

function Palette({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState(0);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const restoreFocus = useRef<Element | null>(document.activeElement);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q), 200);
    return () => window.clearTimeout(id);
  }, [q]);
  useEffect(() => {
    inputRef.current?.focus();
    const back = restoreFocus.current;
    return () => (back instanceof HTMLElement ? back.focus() : undefined);
  }, []);

  const term = debounced.trim();
  const search = useSearch(term);
  const searching = term.length >= 2;

  const groups = useMemo((): { title: string; items: Item[] }[] => {
    if (!searching) {
      const quick: [string, IconName, string][] = [
        ["inbox", "inbox", "/inbox"],
        ["projects", "folder", "/projects"],
        ["notifications", "bell", "/notifications"],
        ["profile", "user", "/profile"],
      ];
      return [
        {
          title: t("search.quickTitle"),
          items: quick.map(([k, icon, path]) => ({ id: `quick-${k}`, icon, label: t(`search.quick.${k}`), path })),
        },
      ];
    }
    const results = search.data ?? [];
    return KINDS.map((kind) => ({
      title: t(`search.groups.${kind}`),
      items: results
        .filter((r) => r.kind === kind)
        .map((r, i) => ({
          id: `${kind}-${r.project_key}-${r.key}-${i}`,
          icon: KIND_ICON[kind],
          label: (
            <>
              <span className="mr-2 font-mono text-sm text-muted">{r.key}</span>
              {r.title}
            </>
          ),
          sub: [r.subtitle, kind === "project" ? "" : r.project_key].filter(Boolean).join(" · "),
          path: r.path,
        })),
    })).filter((g) => g.items.length > 0);
  }, [searching, search.data, t]);

  const flat = groups.flatMap((g) => g.items);
  useEffect(() => setActive(0), [debounced]);
  const current = flat[Math.min(active, flat.length - 1)];

  const go = (item: Item | undefined) => {
    if (!item) return;
    onClose();
    void navigate(item.path);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (flat.length ? (a + 1) % flat.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (flat.length ? (a - 1 + flat.length) % flat.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(current);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-3 pt-[12vh]"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("search.label")}
        className="flex max-h-[70vh] w-full max-w-[620px] flex-col overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface text-text shadow-[var(--shadow)]"
      >
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <Icon name="search" className="text-muted" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("search.placeholder")}
            aria-label={t("search.label")}
            role="combobox"
            aria-expanded={flat.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={current ? `${listId}-${current.id}` : undefined}
            className="h-12 flex-1 border-0 bg-transparent text-[15px] outline-none placeholder:text-muted"
          />
          {searching && search.isFetching && <Spinner />}
        </div>

        <div id={listId} role="listbox" aria-label={t("search.label")} className="min-h-0 flex-1 overflow-y-auto py-1.5">
          {groups.map((g) => (
            <div key={g.title} role="group" aria-label={g.title}>
              <div className="px-4 pt-2 pb-1 label-caps text-muted">{g.title}</div>
              {g.items.map((item) => {
                const selected = item === current;
                return (
                  <div
                    key={item.id}
                    id={`${listId}-${item.id}`}
                    role="option"
                    aria-selected={selected}
                    onMouseMove={() => setActive(flat.indexOf(item))}
                    onClick={() => go(item)}
                    className={cx("mx-1.5 flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2", selected && "bg-accent-soft")}
                  >
                    <Icon name={item.icon} className="shrink-0 text-muted" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{item.label}</span>
                      {item.sub && <span className="block truncate text-xs text-muted">{item.sub}</span>}
                    </span>
                    {selected && <Icon name="right" size={14} className="text-muted" />}
                  </div>
                );
              })}
            </div>
          ))}
          {searching && !search.isPending && flat.length === 0 && (
            <p className="m-0 px-4 py-6 text-center text-sm text-muted">{t("search.noResults", { q: term })}</p>
          )}
          {!searching && q.trim().length === 1 && <p className="m-0 px-4 pb-2 text-xs text-muted">{t("search.minChars")}</p>}
        </div>
        <div className="border-t border-border px-4 py-2 text-xs text-muted">{t("search.hint")}</div>
      </div>
    </div>
  );
}
